// src/utils/fileUtils.ts

import { createLogger } from "./logger";

const log = createLogger("FileUtils");

/**
 * Default supported file types for backward compatibility
 * Individual agents can override these in their configuration
 */
export const SUPPORTED_FILE_EXTENSIONS = [".docx", ".xlsx", ".xls", ".pdf"] as const;
export type SupportedFileExtension = (typeof SUPPORTED_FILE_EXTENSIONS)[number];

/**
 * Default maximum file size in bytes (15MB)
 * Individual agents can override this in their configuration
 */
export const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15MB

/**
 * Default maximum number of files that can be uploaded
 * Individual agents can override this in their configuration
 */
export const MAX_FILE_COUNT = 5;

/**
 * File validation configuration
 * Allows agent-specific limits to be passed to validation functions
 */
export interface FileValidationConfig {
  supportedFileTypes?: string[];
  maxFileSizeMB?: number;
  maxFileCount?: number;
}

/**
 * File validation result interface
 * Provides detailed information about why a file validation failed
 */
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
  errorCode?: "INVALID_TYPE" | "FILE_TOO_LARGE" | "FILE_COUNT_EXCEEDED";
}

/**
 * Validates if a file has an allowed extension
 *
 * @param file - The file to validate
 * @param supportedTypes - Array of supported file extensions (e.g., [".docx", ".xlsx"])
 * @returns true if the file extension is supported
 */
export function hasValidExtension(file: File, supportedTypes?: readonly string[]): boolean {
  const fileName = file.name.toLowerCase();
  const allowedTypes = supportedTypes || SUPPORTED_FILE_EXTENSIONS;
  return allowedTypes.some((ext) => fileName.endsWith(ext.toLowerCase()));
}

/**
 * Gets the file extension from a file name
 *
 * @param fileName - The name of the file
 * @returns The file extension including the dot (e.g., ".docx")
 */
export function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) return "";
  return fileName.substring(lastDotIndex).toLowerCase();
}

/**
 * Validates a single file for upload
 * Checks both file type and size constraints
 *
 * @param file - The file to validate
 * @param config - Optional validation configuration with agent-specific limits
 * @returns Validation result with error details if invalid
 */
export function validateFile(file: File, config?: FileValidationConfig): FileValidationResult {
  // Use spread operator to convert to mutable array if needed
  const supportedTypes = config?.supportedFileTypes || [...SUPPORTED_FILE_EXTENSIONS];
  const maxSizeMB = config?.maxFileSizeMB || MAX_FILE_SIZE_BYTES / (1024 * 1024);
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // Check file extension
  if (!hasValidExtension(file, supportedTypes)) {
    const extension = getFileExtension(file.name);
    log.warn("Invalid file type attempted", {
      fileName: file.name,
      extension,
      supportedExtensions: supportedTypes,
    });

    // Create a user-friendly list of supported types
    const typeList = supportedTypes
      .map((ext) => {
        const cleanExt = ext.replace(".", "").toUpperCase();
        return `.${cleanExt.toLowerCase()} (${cleanExt})`;
      })
      .join(" or ");

    return {
      isValid: false,
      error: `File type not supported. Please upload ${typeList} files only.`,
      errorCode: "INVALID_TYPE",
    };
  }

  // Check file size
  if (file.size > maxSizeBytes) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    log.warn("File too large", {
      fileName: file.name,
      fileSizeMB,
      maxSizeMB,
    });

    return {
      isValid: false,
      error: `File size (${fileSizeMB}MB) exceeds the maximum allowed size of ${maxSizeMB}MB.`,
      errorCode: "FILE_TOO_LARGE",
    };
  }

  return { isValid: true };
}

/**
 * Validates multiple files for upload
 * Checks individual file constraints and total file count
 *
 * @param files - Array of files to validate
 * @param existingFileCount - Number of files already uploaded
 * @param config - Optional validation configuration with agent-specific limits
 * @returns Array of validation results, one per file
 */
export function validateFiles(
  files: File[],
  existingFileCount: number = 0,
  config?: FileValidationConfig
): FileValidationResult[] {
  const maxFileCount = config?.maxFileCount || MAX_FILE_COUNT;

  // Check total file count
  if (existingFileCount + files.length > maxFileCount) {
    const remainingSlots = maxFileCount - existingFileCount;
    log.warn("File count exceeded", {
      attempting: files.length,
      existing: existingFileCount,
      max: maxFileCount,
      remainingSlots,
    });

    // Create appropriate error message based on max file count
    let errorMessage: string;
    if (maxFileCount === 1) {
      errorMessage = "You can only upload 1 file.";
    } else {
      errorMessage = `You can only upload up to ${maxFileCount} files. You have ${remainingSlots} slot${remainingSlots === 1 ? "" : "s"} remaining.`;
    }

    // Return error for all files
    return files.map(() => ({
      isValid: false,
      error: errorMessage,
      errorCode: "FILE_COUNT_EXCEEDED" as const,
    }));
  }

  // Validate each file individually with the provided config
  return files.map((file) => validateFile(file, config));
}

/**
 * Truncates a file name if it's too long for display
 * Keeps the extension visible for clarity
 *
 * @param fileName - The file name to truncate
 * @param maxLength - Maximum length before truncation (default: 25)
 * @returns Truncated file name with ellipsis if needed
 */
export function truncateFileName(fileName: string, maxLength: number = 25): string {
  if (fileName.length <= maxLength) {
    return fileName;
  }

  const extension = getFileExtension(fileName);
  const nameWithoutExtension = fileName.substring(0, fileName.length - extension.length);

  // Calculate how much of the name we can show
  const availableLength = maxLength - extension.length - 3; // 3 for "..."

  if (availableLength <= 0) {
    // If even the extension is too long, just truncate everything
    return fileName.substring(0, maxLength - 3) + "...";
  }

  // Truncate the name part and keep the extension
  const truncatedName = nameWithoutExtension.substring(0, availableLength) + "...";
  return truncatedName + extension;
}

/**
 * Formats a file size in bytes to a human-readable string
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

/**
 * Creates a unique identifier for a file
 * Useful for React keys and tracking files
 *
 * @param file - The file to create an ID for
 * @returns A unique string identifier
 */
export function createFileId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

/**
 * Checks if a file is a Word document
 *
 * @param file - The file to check
 * @returns true if the file is a .docx file
 */
export function isWordDocument(file: File): boolean {
  return getFileExtension(file.name) === ".docx";
}

/**
 * Checks if a file is an Excel spreadsheet
 *
 * @param file - The file to check
 * @returns true if the file is a .xlsx or .xls file
 */
export function isExcelDocument(file: File): boolean {
  const extension = getFileExtension(file.name);
  return extension === ".xlsx" || extension === ".xls";
}

/**
 * Checks if a file is a PDF document
 *
 * @param file - The file to check
 * @returns true if the file is a .pdf file
 */
export function isPDFDocument(file: File): boolean {
  const extension = getFileExtension(file.name);
  return extension === ".pdf";
}
